// DevHub: header profile dropdown (admin views for admins, settings, dark-mode
// toggle, partner-portal preview, sign out). Extracted from Root.tsx.
//
// Admin lives here rather than in the top navigation: the top bar is the product's
// information architecture, and the admin views are an account-level capability of
// the signed-in person, like Settings. This is also the only admin entry point —
// the mobile hamburger no longer carries one, since this dropdown renders at every
// width.
import { useNavigate } from 'react-router-dom';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { featureEnabled } from '@internal/plugin-isdp-common';
import { commonMessages } from '../../i18n/common';
import { ROUTES } from '../common/constants/routes';
import { readLastPreviewRole, writePreviewRole } from '../auth/previewRole';
import { ProfileAvatar } from '../settings/ProfileAvatar';
import { useDropdown } from './useDropdown';
import { adminItems } from './navConfig';

export function ProfileDropdown({
  isAdmin,
  isDark,
  toggleDark,
  onSignOut,
}: {
  isAdmin: boolean;
  isDark: boolean;
  toggleDark: () => void;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const { open, setOpen, ref } = useDropdown();
  const { t } = useTranslationRef(commonMessages);
  const partnerPortal = featureEnabled(useApi(configApiRef), 'partnerPortal');

  return (
    <div className="devhub-profile-dropdown" ref={ref}>
      <button
        className={`odx-button odx-button--ghost ${open ? 'is-active' : ''}`}
        type="button"
        onClick={() => setOpen(o => !o)}
        title={t('profile.account')}
      >
        {/* Shows the Okta profile picture when available; falls back to the
            user's initials, then the core::user icon (guest / no picture). */}
        <ProfileAvatar size={26} />
      </button>
      {open && (
        <div className="devhub-profile-dropdown__menu">
          {/* Admin views — admins only. Same `adminItems` the nav used, so the set and
              its icons stay defined in one place (navConfig.ts). */}
          {isAdmin && (
            <>
              <div className="devhub-profile-dropdown__label">{t('admin.admin')}</div>
              {adminItems.map(item => (
                <button
                  key={item.path}
                  className="devhub-profile-dropdown__item"
                  type="button"
                  onClick={() => {
                    navigate(item.path);
                    setOpen(false);
                  }}
                >
                  <odx-icon className="odx-icon" name={item.icon} size="sm" />
                  {(t as unknown as (k: string) => string)(item.labelKey)}
                </button>
              ))}
              <div className="devhub-profile-dropdown__divider" />
            </>
          )}
          <button
            className="devhub-profile-dropdown__item"
            type="button"
            onClick={() => { navigate(ROUTES.settings); setOpen(false); }}
          >
            <odx-icon className="odx-icon" name="core::settings" size="sm" />
            {t('profile.settings')}
          </button>
          <button
            className="devhub-profile-dropdown__item"
            type="button"
            onClick={() => { toggleDark(); setOpen(false); }}
          >
            <odx-icon className="odx-icon" name={isDark ? 'core::brightness' : 'core::night-mode'} size="sm" />
            {isDark ? t('profile.lightMode') : t('profile.darkMode')}
          </button>
          {/* ISDP CUSTOM: internal users can preview the external Partner Portal as an
              external visitor (e.g. a sales rep presenting to a partner). A full navigation lets
              useUserRole re-read the preview flag and render World A in-app.
              Hidden while the portal is out of the release (isdp-common/release.ts) —
              there would be nothing to preview. */}
          {partnerPortal && (
            <button
              className="devhub-profile-dropdown__item"
              type="button"
              onClick={() => {
                // Reopen the face this reviewer last previewed (anonymous, a chosen set
                // of products, …); the first time it is a partner with every product.
                writePreviewRole(readLastPreviewRole() ?? 'partner');
                window.location.assign(ROUTES.public.products);
              }}
            >
              <odx-icon className="odx-icon" name="core::view" size="sm" />
              {t('profile.viewPartnerPortal')}
            </button>
          )}
          <div className="devhub-profile-dropdown__divider" />
          <button
            className="devhub-profile-dropdown__item devhub-profile-dropdown__item--danger"
            type="button"
            onClick={() => { onSignOut(); setOpen(false); }}
          >
            <odx-icon className="odx-icon" name="core::link-external" size="sm" />
            {t('profile.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
